import {
  type DataSourceTableViewLayoutState,
  type DataSourceViewLayoutState,
} from '@blocksuite/data-view';

import { type DatabaseLocalUiState } from './data-source-contracts.js';

export const DATABASE_LOCAL_UI_STATE_STORAGE_KEY =
  'sl:database-local-ui-state:v2';

export const DATABASE_VIEW_LAYOUT_STATE_VERSION = 1 as const;

export const DATABASE_TABLE_LAYOUT_STATE_VERSION = 1 as const;

export const DATABASE_TABLE_LAYOUT_ROW_HEIGHT_LIMIT = 5000;

export function omitDatabaseLocalUiStateView<T>(
  state: Record<string, T> | undefined,
  viewId: string
): Record<string, T> | undefined {
  if (!state || !(viewId in state)) {
    return state;
  }
  const { [viewId]: _deleted, ...next } = state;
  return Object.keys(next).length > 0 ? next : undefined;
}

export function getLocalUiStorageKey(
  databaseRootDocId: string,
  databaseId: string
): string {
  return `${DATABASE_LOCAL_UI_STATE_STORAGE_KEY}:${databaseRootDocId}:${databaseId}`;
}

export function readDatabaseLocalUiState(
  databaseRootDocId: string,
  databaseId: string
): DatabaseLocalUiState {
  if (typeof localStorage === 'undefined') {
    return {};
  }
  try {
    const raw = localStorage.getItem(
      getLocalUiStorageKey(databaseRootDocId, databaseId)
    );
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as DatabaseLocalUiState;
    if (!parsed || typeof parsed !== 'object') {
      return {};
    }
    return {
      tableLayouts:
        parsed.tableLayouts && typeof parsed.tableLayouts === 'object'
          ? sanitizeDatabaseTableLayoutStates(parsed.tableLayouts)
          : undefined,
      viewLayouts:
        parsed.viewLayouts && typeof parsed.viewLayouts === 'object'
          ? sanitizeDatabaseViewLayoutStates(parsed.viewLayouts)
          : undefined,
      viewId: typeof parsed.viewId === 'string' ? parsed.viewId : undefined,
      windows:
        parsed.windows && typeof parsed.windows === 'object'
          ? parsed.windows
          : undefined,
    };
  } catch {
    return {};
  }
}

export function writeDatabaseLocalUiState(
  databaseRootDocId: string,
  databaseId: string,
  state: DatabaseLocalUiState
): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    const sanitizedState: DatabaseLocalUiState = {
      tableLayouts:
        state.tableLayouts && typeof state.tableLayouts === 'object'
          ? sanitizeDatabaseTableLayoutStates(state.tableLayouts)
          : undefined,
      viewLayouts:
        state.viewLayouts && typeof state.viewLayouts === 'object'
          ? sanitizeDatabaseViewLayoutStates(state.viewLayouts)
          : undefined,
      viewId: typeof state.viewId === 'string' ? state.viewId : undefined,
      windows:
        state.windows && typeof state.windows === 'object'
          ? state.windows
          : undefined,
    };
    localStorage.setItem(
      getLocalUiStorageKey(databaseRootDocId, databaseId),
      JSON.stringify(sanitizedState)
    );
  } catch {
    // Browser storage is best-effort local UI state.
  }
}

export function sanitizeDatabaseViewLayoutStates(
  viewLayouts: Record<string, unknown>
): Record<string, DataSourceViewLayoutState> | undefined {
  const sanitized: Record<string, DataSourceViewLayoutState> = {};
  for (const [viewId, state] of Object.entries(viewLayouts)) {
    if (!state || typeof state !== 'object') {
      continue;
    }
    const candidate = state as Partial<DataSourceViewLayoutState>;
    if (
      candidate.version !== DATABASE_VIEW_LAYOUT_STATE_VERSION ||
      typeof candidate.viewMode !== 'string' ||
      typeof candidate.intrinsicContentHeight !== 'number' ||
      !Number.isFinite(candidate.intrinsicContentHeight) ||
      candidate.intrinsicContentHeight <= 0
    ) {
      continue;
    }
    sanitized[viewId] = {
      intrinsicContentHeight: candidate.intrinsicContentHeight,
      measuredAt:
        typeof candidate.measuredAt === 'number' &&
        Number.isFinite(candidate.measuredAt)
          ? candidate.measuredAt
          : 0,
      viewMode: candidate.viewMode,
      version: DATABASE_VIEW_LAYOUT_STATE_VERSION,
    };
  }
  return Object.keys(sanitized).length > 0 ? sanitized : undefined;
}

export function sanitizeDatabaseTableLayoutStates(
  tableLayouts: Record<string, unknown>
): Record<string, DataSourceTableViewLayoutState> | undefined {
  const sanitized: Record<string, DataSourceTableViewLayoutState> = {};
  for (const [viewId, state] of Object.entries(tableLayouts)) {
    if (!state || typeof state !== 'object') {
      continue;
    }
    const candidate = state as Partial<DataSourceTableViewLayoutState>;
    if (
      candidate.version !== DATABASE_TABLE_LAYOUT_STATE_VERSION ||
      typeof candidate.columnLayoutSignature !== 'string' ||
      !candidate.rowHeights ||
      typeof candidate.rowHeights !== 'object'
    ) {
      continue;
    }
    const rowHeights: Record<string, number> = {};
    for (const [rowId, height] of Object.entries(candidate.rowHeights)) {
      if (
        typeof rowId !== 'string' ||
        typeof height !== 'number' ||
        !Number.isFinite(height) ||
        height <= 0
      ) {
        continue;
      }
      rowHeights[rowId] = height;
      if (
        Object.keys(rowHeights).length >= DATABASE_TABLE_LAYOUT_ROW_HEIGHT_LIMIT
      ) {
        break;
      }
    }
    sanitized[viewId] = {
      columnLayoutSignature: candidate.columnLayoutSignature,
      intrinsicContentHeight:
        typeof candidate.intrinsicContentHeight === 'number' &&
        Number.isFinite(candidate.intrinsicContentHeight) &&
        candidate.intrinsicContentHeight > 0
          ? candidate.intrinsicContentHeight
          : undefined,
      measuredAt:
        typeof candidate.measuredAt === 'number' &&
        Number.isFinite(candidate.measuredAt)
          ? candidate.measuredAt
          : 0,
      rowHeights,
      version: DATABASE_TABLE_LAYOUT_STATE_VERSION,
    };
  }
  return Object.keys(sanitized).length > 0 ? sanitized : undefined;
}
